import { useEffect, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { CalendarDays, Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getRotinaAgendamento, type Rotina } from '@/services/api/rotinas';
import NovaRotinaDialog from './NovaRotinaDialog';
import RotinaCard from './RotinaCard';
import { DIAS_SEMANA, HORA_MATERIALIZACAO } from './rotinaUtils';

/**
 * O dia selecionado. Com mais de uma Rotina no dia, uma chip por Rotina e uma
 * aberta por vez — não dá para ligar duas, então mostrar duas lado a lado só
 * sugeriria que dá.
 */
export default function RotinaDia({ dia, rotinas, onImportar, podeCriar = true, somenteLeitura }: {
    dia: number;
    /** Só as Rotinas deste dia. */
    rotinas: Rotina[];
    onImportar: (dia: number) => void;
    /** Admin vendo Rotinas de outro usuário não cria nelas. */
    podeCriar?: boolean;
    somenteLeitura?: (r: Rotina) => boolean;
}) {
    const [nova, setNova] = useState(false);
    const [abertaId, setAbertaId] = useState<number | null>(null);

    // A Rotina no ar é a que interessa primeiro; sem nenhuma no ar, a primeira da lista.
    const preferida = rotinas.find((e) => e.ativa) ?? rotinas[0];
    const aberta = rotinas.find((e) => e.id === abertaId) ?? preferida;

    useEffect(() => { setAbertaId(null); }, [dia]);

    // Mesma queryKey do RotinaCard: o React Query serve as duas do mesmo cache,
    // então a contagem da chip não custa requisição extra.
    const contagens = useQueries({
        queries: rotinas.map((e) => ({
            queryKey: ['rotina-agendamento', e.id],
            queryFn: () => getRotinaAgendamento(e.id),
        })),
    });
    const gruposDe = (id: number) => {
        const i = rotinas.findIndex((e) => e.id === id);
        return i >= 0 ? contagens[i]?.data?.grupos.length : undefined;
    };

    // No ar primeiro, depois alfabética: numa lista de oito, a que está no ar é
    // a que se procura. Ordem estável — não depende de qual está aberta.
    const ordenadas = [...rotinas].sort(
        (a, b) => (b.ativa ? 1 : 0) - (a.ativa ? 1 : 0) || a.nome.localeCompare(b.nome, 'pt-BR'),
    );

    if (rotinas.length === 0) {
        return (
            <section className="flex flex-col items-center gap-3 rounded-xl border border-dashed bg-card px-6 py-14 text-center">
                <CalendarDays className="h-7 w-7 text-muted-foreground" />
                <h2 className="text-base font-semibold">{DIAS_SEMANA[dia]} ainda não tem Rotina</h2>
                <p className="max-w-md text-sm text-muted-foreground">
                    Sem Rotina no ar, nenhum broadcast é criado às {HORA_MATERIALIZACAO} deste dia.
                </p>
                {podeCriar && (
                    <div className="mt-1 flex flex-wrap justify-center gap-2">
                        <Button variant="outline" onClick={() => onImportar(dia)}>Montar a partir de um dia</Button>
                        <Button onClick={() => setNova(true)}>Nova Rotina</Button>
                    </div>
                )}
                <NovaRotinaDialog open={nova} onOpenChange={setNova} dia={dia} />
            </section>
        );
    }

    return (
        <section className="flex flex-col gap-4">
            {/* Sem `justify-between`: os botões que ocupavam a direita desta linha
                saíram (já existem no cabeçalho da página), e empurrar o seletor
                sozinho para a borda deixaria um vão no meio. */}
            <div className="flex flex-wrap items-center gap-2">
                {/* Seletor, não abas: o gestor cria Rotina à vontade e em oito elas
                    já quebravam para a segunda linha. O dropdown ocupa uma linha
                    fixa por mais Rotinas que existam. */}
                {rotinas.length > 1 ? (
                    <div className="flex items-center gap-2">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" className="h-9 max-w-[22rem] justify-between gap-2 px-3">
                                    <span className="flex min-w-0 items-center gap-2">
                                        <span
                                            className={`h-2 w-2 shrink-0 rounded-full ${aberta?.ativa ? 'bg-emerald-500' : 'bg-amber-500'}`}
                                            aria-hidden
                                        />
                                        <span className="truncate font-medium">{aberta?.nome}</span>
                                        {aberta?.owner_name && (
                                            <span className="shrink-0 text-xs font-normal text-muted-foreground">· {aberta.owner_name}</span>
                                        )}
                                        <span className="shrink-0 text-xs font-normal tabular-nums text-muted-foreground">
                                            {gruposDe(aberta?.id ?? -1) ?? '…'} grupos
                                        </span>
                                    </span>
                                    <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="max-h-[22rem] w-[22rem] overflow-y-auto">
                                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                                    {rotinas.length} Rotinas de {DIAS_SEMANA[dia]} · podem estar no ar juntas
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                {ordenadas.map((e) => {
                                    const n = gruposDe(e.id);
                                    return (
                                        <DropdownMenuItem
                                            key={e.id}
                                            onSelect={() => setAbertaId(e.id)}
                                            className="flex items-center gap-2"
                                        >
                                            <span
                                                className={`h-2 w-2 shrink-0 rounded-full ${e.ativa ? 'bg-emerald-500' : 'bg-amber-500'}`}
                                                aria-hidden
                                            />
                                            <span className={`min-w-0 flex-1 truncate ${e.id === aberta?.id ? 'font-semibold' : ''}`}>
                                                {e.nome}
                                                {e.owner_name && <span className="font-normal text-muted-foreground"> · {e.owner_name}</span>}
                                            </span>
                                            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                                                {e.ativa ? 'no ar · ' : ''}{n ?? '…'} grupos
                                            </span>
                                            {e.id === aberta?.id && <Check className="h-4 w-4 shrink-0" />}
                                        </DropdownMenuItem>
                                    );
                                })}
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <span className="text-[13px] text-muted-foreground">
                            Podem ficar várias no ar, desde que não dividam página.
                        </span>
                    </div>
                ) : (
                    <span className="text-[13px] text-muted-foreground">
                        Única Rotina de {DIAS_SEMANA[dia]}.
                    </span>
                )}
            </div>

            {aberta && (
                <RotinaCard
                    key={aberta.id}
                    rotina={aberta}
                    outraNoAr={rotinas.find((e) => e.ativa && e.id !== aberta.id && e.user_id === aberta.user_id)}
                    somenteLeitura={somenteLeitura?.(aberta)}
                />
            )}

            <NovaRotinaDialog open={nova} onOpenChange={setNova} dia={dia} />
        </section>
    );
}
